import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/store";
import { leadsService } from "@/feature/leads/data/leadsService";
import type { Lead } from "@/feature/leads/types";

interface DailySalesDashboardState {
  metrics: any | null;
  overdueFollowups: any[];
  followupsDueToday: any[];
  todayAppointments: any[];
  loading: boolean;
  error: string | null;
}

interface Appointment {
  id: string;
  time: string;
  title: string;
  location: string;
  type: 'site' | 'showroom' | 'virtual';
  tag: string;
  tagColor: 'success' | 'primary' | 'secondary';
  details?: string;
}

interface FollowUp {
  id: string;
  time: string;
  name: string;
  description: string;
  status?: string;
  actionType: 'call' | 'email';
  phone?: string;
  whatsapp?: string;
  currentStage?: string;
}

interface OverdueFollowUp {
  id: string;
  name: string;
  contact: string;
  interest: string;
  lateBy: string;
  actionType: 'call' | 'email';
  phone?: string;
  whatsapp?: string;
}

const initialState: DailySalesDashboardState = {
  metrics: null,
  overdueFollowups: [],
  followupsDueToday: [],
  todayAppointments: [],
  loading: false,
  error: null,
};

// Helper function to check if a date is today
const isToday = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const appointmentDate = new Date(date);
  appointmentDate.setHours(0, 0, 0, 0);
  return appointmentDate.getTime() === today.getTime();
};

// Helper function to check if a date is in the past (before today)
const isPastDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const followUpDate = new Date(date);
  followUpDate.setHours(0, 0, 0, 0);
  return followUpDate.getTime() < today.getTime();
};

// Helper function to calculate days overdue
const calculateDaysOverdue = (dateString: string): number => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const followUpDate = new Date(date);
  followUpDate.setHours(0, 0, 0, 0);
  const diffTime = today.getTime() - followUpDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to format "late by" text
const formatLateBy = (days: number): string => {
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month' : `${months} months`;
};

// Helper function to transform overdue follow-up
const transformOverdueFollowUp = (
  followUp: any,
  lead: Lead,
  daysOverdue: number
): OverdueFollowUp | null => {
  const followUpType = (followUp.type || '').toLowerCase();
  const actionType: 'call' | 'email' = followUpType === 'phone' ? 'call' : 'email';

  // Get contact info
  const phone = lead.phone || lead.contact?.phone || '';
  const email = lead.email || lead.contact?.email || '';
  const contact = actionType === 'call' ? phone : email;

  return {
    id: followUp.id,
    name: lead.name || lead.contact?.name || 'Lead',
    contact: contact || 'N/A',
    interest: lead.currentStage || lead.detailedStage || lead.stage || 'N/A',
    lateBy: formatLateBy(daysOverdue),
    actionType,
    phone: lead.phone || lead.contact?.phone,
    whatsapp: lead.whatsapp || lead.contact?.whatsapp,
  };
};

// Helper function to format time from date string
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper function to transform lead follow-up to appointment
const transformFollowUpToAppointment = (
  followUp: any,
  lead: Lead
): Appointment | null => {
  const followUpType = (followUp.type || '').toLowerCase();
  const isSiteVisit = followUpType === 'sitevisit';
  const appointmentType = isSiteVisit ? 'site' : 'showroom';

  // Get location from lead
  const location = isSiteVisit
    ? lead.siteAddress || lead.physicalAddress || lead.siteName || 'Site Address'
    : lead.physicalAddress || 'Showroom';

  return {
    id: followUp.id,
    time: formatTime(followUp.date),
    title: lead.name || lead.contact?.name || 'Lead',
    location: location,
    type: appointmentType,
    tag: followUp.channel || 'Appointment',
    tagColor: followUp.outcome === 'Scheduled' ? 'primary' : 'success',
    details: followUp.summary,
  };
};

// Helper function to transform follow-up history item to FollowUp format
const transformFollowUpHistoryToFollowUp = (
  followUp: any,
  lead: Lead
): FollowUp | null => {
  const followUpType = (followUp.type || '').toLowerCase();

  // Only include phone and email follow-ups (not site/store visits - those are appointments)
  if (followUpType !== 'phone' && followUpType !== 'email') {
    return null;
  }

  // Determine action type
  const actionType: 'call' | 'email' = followUpType === 'phone' ? 'call' : 'email';

  return {
    id: followUp.id,
    time: formatTime(followUp.date),
    name: lead.name || lead.contact?.name || 'Lead',
    description: followUp.summary || followUp.channel || 'Follow-up',
    status: followUp.outcome === 'Scheduled' ? 'Scheduled' : undefined,
    actionType,
    phone: lead.phone || lead.contact?.phone,
    whatsapp: lead.whatsapp || lead.contact?.whatsapp,
    currentStage: lead.currentStage || lead.detailedStage || lead.stage,
  };
};

// Fetch dashboard data including today's appointments from leads
export const fetchDashboard = createAsyncThunk(
    "dailySalesDashboard/fetchDashboard",
    async (_, { rejectWithValue }) => {
        try {
            const response = await leadsService.getSalesDashboard();

            if (!response.success || !response.data) {
                return rejectWithValue(response.error?.message || 'Failed to fetch dashboard data');
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch dashboard data');
        }
    }
);

const dailySalesDashboardSlice = createSlice({
  name: "dailySalesDashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.overdueFollowups = action.payload.overdueFollowups;
        state.followupsDueToday = action.payload.followupsDueToday;
        state.todayAppointments = action.payload.todayAppointments;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || "Failed to load daily sales dashboard";
      });
  },
});

export default dailySalesDashboardSlice.reducer;

export const selectMetrics = (state: RootState) => state.dailySalesDashboard.metrics;
export const selectOverdueFollowups = (state: RootState) =>
  state.dailySalesDashboard.overdueFollowups;
export const selectFollowupsDueToday = (state: RootState) =>
  state.dailySalesDashboard.followupsDueToday;
export const selectTodayAppointments = (state: RootState) =>
  state.dailySalesDashboard.todayAppointments;
export const selectLoading = (state: RootState) => state.dailySalesDashboard.loading;
export const selectError = (state: RootState) => state.dailySalesDashboard.error;


